// The data pipeline between the radar and the flak cannon. Pure logic: no DOM, no canvas.
//
// A radar paint becomes a Contact. The contact passes through the player's blocks in order;
// each block adds latency and may drop it. A contact that survives becomes a FireOrder for the cannon.

export type BlockKind = "iff" | "confirm" | "dedupe" | "predict" | "sort";

export interface BlockDef {
  kind: BlockKind;
  label: string;
  tag: string;
  description: string;
  latencyMs: number;
}

export const BLOCKS: Record<BlockKind, BlockDef> = {
  iff: {
    kind: "iff",
    label: "IFF Filter",
    tag: "filter",
    description: "Drops contacts that answer as friendly, so the cannon never fires at blue aircraft.",
    latencyMs: 60,
  },
  confirm: {
    kind: "confirm",
    label: "Track Confirm",
    tag: "filter",
    description: "Passes a track only once the radar has painted it twice. Drops one-off jamming ghosts.",
    latencyMs: 40,
  },
  dedupe: {
    kind: "dedupe",
    label: "Dedupe",
    tag: "filter",
    description: "Passes one contact per track every 1.2 s. Without it, each radar sweep fires several shells at the same aircraft.",
    latencyMs: 40,
  },
  predict: {
    kind: "predict",
    label: "Lead Predictor",
    tag: "transform",
    description:
      "Aims where the target will be when the shell arrives. It allows for latency before it, but not after it: keep it close to the cannon.",
    latencyMs: 80,
  },
  sort: {
    kind: "sort",
    label: "Threat Sort",
    tag: "router",
    description: "The cannon engages the target closest to escaping first, rather than the oldest contact.",
    latencyMs: 120,
  },
};

export const BLOCK_ORDER: BlockKind[] = ["iff", "confirm", "dedupe", "predict", "sort"];
export const SLOT_COUNT = 5;
export const DEDUPE_WINDOW_S = 1.2;
export const SHELL_SPEED = 620; // px/s

export type Slots = (BlockKind | null)[];

export interface Point {
  x: number;
  y: number;
}

export interface Contact {
  trackId: number;
  /** Position when painted. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  friendly: boolean;
  /** Number of times the radar has painted this track, this paint included. */
  hits: number;
  /** Sim time (s) of the paint. */
  paintedAt: number;
}

export interface FireOrder {
  contact: Contact;
  aim: Point;
  /** Sim time (s) the order reaches the cannon. */
  readyAt: number;
  predicted: boolean;
}

export interface TraceStep {
  slot: number;
  at: number;
  passed: boolean;
  reason?: string;
}

export interface PipelineResult {
  order: FireOrder | null;
  trace: TraceStep[];
}

/** State that outlives a single contact: the Dedupe block's memory. */
export class PipelineState {
  private lastPassed = new Map<number, number>();

  dedupe(trackId: number, at: number): boolean {
    const last = this.lastPassed.get(trackId);
    if (last !== undefined && at - last < DEDUPE_WINDOW_S) return false;
    this.lastPassed.set(trackId, at);
    return true;
  }

  forget(trackId: number): void {
    this.lastPassed.delete(trackId);
  }
}

export function positionAt(c: Contact, t: number): Point {
  const dt = t - c.paintedAt;
  return { x: c.x + c.vx * dt, y: c.y + c.vy * dt };
}

/** Where to aim so a shell fired at `fireAt` from `gun` meets the contact. */
export function interceptPoint(c: Contact, gun: Point, fireAt: number): Point {
  let aim = positionAt(c, fireAt);
  for (let i = 0; i < 4; i++) {
    const flight = Math.hypot(aim.x - gun.x, aim.y - gun.y) / SHELL_SPEED;
    aim = positionAt(c, fireAt + flight);
  }
  return aim;
}

export function totalLatencyMs(slots: Slots): number {
  return slots.reduce((sum, k) => sum + (k ? BLOCKS[k].latencyMs : 0), 0);
}

/** Latency the Lead Predictor cannot allow for: every block after it. Null when there is no predictor. */
export function latencyAfterPredictorMs(slots: Slots): number | null {
  const i = slots.indexOf("predict");
  if (i < 0) return null;
  return totalLatencyMs(slots.slice(i + 1));
}

export function runPipeline(contact: Contact, slots: Slots, state: PipelineState, gun: Point): PipelineResult {
  const trace: TraceStep[] = [];
  let t = contact.paintedAt;
  let aim: Point = { x: contact.x, y: contact.y };
  let predicted = false;

  for (let slot = 0; slot < slots.length; slot++) {
    const kind = slots[slot];
    if (!kind) continue;
    t += BLOCKS[kind].latencyMs / 1000;

    let reason: string | undefined;
    switch (kind) {
      case "iff":
        if (contact.friendly) reason = "friendly";
        break;
      case "confirm":
        if (contact.hits < 2) reason = "unconfirmed";
        break;
      case "dedupe":
        if (!state.dedupe(contact.trackId, t)) reason = "duplicate";
        break;
      case "predict":
        aim = interceptPoint(contact, gun, t);
        predicted = true;
        break;
      case "sort":
        break;
    }

    trace.push({ slot, at: t, passed: !reason, reason });
    if (reason) return { order: null, trace };
  }

  return { order: { contact, aim, readyAt: t, predicted }, trace };
}

/** Seconds until the contact leaves the screen; the Threat Sort block engages the smallest first. */
export function timeToEscape(c: Contact, now: number, width: number): number {
  const p = positionAt(c, now);
  if (c.vx > 0) return (width - p.x) / c.vx;
  if (c.vx < 0) return p.x / -c.vx;
  return Infinity;
}
